// TripDetails — Phase 4 Plan 04-07 (DIS-18, UI-SPEC §Screen 4); migrated to
// telegram-ui in Phase 12 Plan 12-07 (UI-SPEC §7 / §221).
//
// telegram-ui migration:
//   - native BackButton (nested screen) + in-DOM fallback (testid preserved);
//   - <TabsList> replaces hand-rolled role="tab" divs;
//   - tabs Обзор/Маршрут/События/CMR (overview / route / timeline=events /
//     documents) + owner-only Visibility;
//   - Маршрут → <Timeline> of 2-20 stops (R3: current stop = accent via
//     `active` index; passed stops = pre-active/muted);
//   - ETA + status surfaced as a <Cell> at the top of Обзор;
//   - local decodeJwtPayload consolidated onto canonical lib/jwt (tech-debt
//     paid down per plan Task 2 read_first);
//   - data-fetch (single GET + tab switching), visibility PATCH and all
//     data-testid preserved (RESEARCH Pitfall 6); no hex / no font overrides.
//
// Visibility tab uses PATCH /api/v1/trips/{id}/visibility (Plan 04-04). ETA +
// Status switches are locked on by D-71 GDPR Art. 25 per visibility_service.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Avatar,
  Banner,
  Button,
  Cell,
  List,
  Section,
  Switch,
  TabsList,
  Timeline,
} from "@telegram-apps/telegram-ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { type TripDetailsResponse, tripsApi } from "../api/trips";
import { useAuth } from "../auth/useAuth";
import { EtaCard } from "../components/EtaCard";
import { decodeJwtPayload } from "../lib/jwt";
import { SAFE_AREA_BOTTOM } from "../lib/safeArea";
import { useBackButton } from "../lib/telegramButtons";

type TabKey = "overview" | "route" | "timeline" | "documents" | "visibility";

// Toggleable fields (must agree with VisibilityResponse keys).
const TOGGLEABLE_FIELDS = [
  "driver_name",
  "driver_contact",
  "license_plate",
  "carrier_name",
] as const;

export function TripDetails(): React.JSX.Element {
  const { t } = useTranslation();
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { apiClient, jwt } = useAuth();
  const queryClient = useQueryClient();

  // Native back navigation on this nested screen (UI-SPEC §Platform Contract).
  useBackButton(() => navigate(-1));

  const tripIdNum = useMemo(() => {
    const n = Number.parseInt(tripId ?? "", 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [tripId]);

  const role = useMemo(() => decodeJwtPayload(jwt)?.role ?? null, [jwt]);
  const isOwner = role === "owner";

  const [tab, setTab] = useState<TabKey>("overview");
  const [localVisibility, setLocalVisibility] = useState<Record<string, boolean> | null>(null);

  const tripQuery = useQuery<TripDetailsResponse>({
    queryKey: ["trip-details", tripIdNum],
    enabled: tripIdNum !== null,
    queryFn: () => {
      if (tripIdNum === null) {
        throw new Error("tripId required");
      }
      return tripsApi.details(apiClient, tripIdNum);
    },
  });

  const visibilityMutation = useMutation({
    mutationFn: async (updates: Record<string, boolean>) => {
      if (tripIdNum === null) throw new Error("tripId required");
      const idempotencyKey =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      return apiClient.fetch(`/api/v1/trips/${tripIdNum}/visibility`, {
        method: "PATCH",
        headers: { "Idempotency-Key": idempotencyKey },
        body: JSON.stringify(updates),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trip-details", tripIdNum] });
    },
  });

  if (tripIdNum === null) {
    return (
      <List data-testid="trip-details-invalid">
        <Section>
          <Cell>{t("dispatcher:trip.error.invalid_id", "Invalid trip id") as string}</Cell>
        </Section>
      </List>
    );
  }

  if (tripQuery.isLoading) {
    return (
      <List data-testid="trip-details-loading">
        <Section>
          <Cell>{t("dispatcher:common.loading", "Loading…") as string}</Cell>
        </Section>
      </List>
    );
  }
  if (tripQuery.isError || !tripQuery.data) {
    return (
      <List data-testid="trip-details-error">
        <Section>
          <Cell>{t("dispatcher:trip.error.load", "Failed to load trip.") as string}</Cell>
        </Section>
      </List>
    );
  }

  const trip = tripQuery.data;
  const visibilitySettings = localVisibility ?? trip.visibility_settings;

  // R3: current stop = first stop not yet arrived; passed stops are muted.
  const orderedStops = [...trip.stops].sort((a, b) => a.sequence_idx - b.sequence_idx);
  const currentStopIdx = orderedStops.findIndex((s) => s.arrived_at === null);
  // active index for telegram-ui Timeline: how many stops are completed.
  const timelineActive = currentStopIdx < 0 ? orderedStops.length : currentStopIdx;

  const contentTabs: { key: TabKey; labelKey: string; fallback: string }[] = [
    { key: "overview", labelKey: "dispatcher:trip.tab.overview", fallback: "Overview" },
    { key: "route", labelKey: "dispatcher:trip.tab.route", fallback: "Route" },
    { key: "timeline", labelKey: "dispatcher:trip.tab.timeline", fallback: "Events" },
    { key: "documents", labelKey: "dispatcher:trip.tab.documents", fallback: "CMR" },
  ];

  return (
    <List data-testid="trip-details">
      <Section>
        {/* Native BackButton handles back nav; in-DOM fallback keeps the action
            (and its testid) for web/desktop and tests. */}
        <Cell
          Component="button"
          data-testid="trip-details-back"
          onClick={() => navigate(-1)}
          before={<span aria-hidden>←</span>}
        >
          {t("dispatcher:common.back", "Back") as string}
        </Cell>
        <Cell
          subhead={t("dispatcher:trip.title", "Trip #{{label}}", {
            label: trip.external_id ?? trip.id,
          })}
          data-testid="trip-details-subhead"
          subtitle={
            trip.driver_id !== null
              ? (t("dispatcher:trip.row.driver", "Driver #{{id}}", {
                  id: trip.driver_id,
                }) as string)
              : (t("dispatcher:trip.row.no_driver", "Unassigned") as string)
          }
        >
          {t(`dispatcher:trip.status.${trip.status}`, trip.status) as string}
        </Cell>
      </Section>

      <TabsList data-testid="trip-details-tabs">
        {[
          ...contentTabs.map((c) => (
            <TabsList.Item
              key={c.key}
              selected={tab === c.key}
              data-testid={`trip-details-tab-${c.key}`}
              onClick={() => setTab(c.key)}
            >
              {t(c.labelKey, c.fallback) as string}
            </TabsList.Item>
          )),
          // Owner-only Visibility tab (UI-SPEC: hidden for dispatcher role).
          isOwner ? (
            <TabsList.Item
              key="visibility"
              selected={tab === "visibility"}
              data-testid="trip-details-tab-visibility"
              onClick={() => setTab("visibility")}
            >
              {t("dispatcher:trip.tab.visibility", "Visibility") as string}
            </TabsList.Item>
          ) : (
            <TabsList.Item key="visibility-hidden" style={{ display: "none" }} aria-hidden />
          ),
        ]}
      </TabsList>

      {tab === "overview" && (
        <Section data-testid="trip-details-overview">
          <Cell
            data-testid="trip-details-eta-cell"
            before={<Avatar size={40} acronym="ETA" />}
            subtitle={t(`dispatcher:trip.status.${trip.status}`, trip.status) as string}
            multiline
          >
            {t("dispatcher:trip.eta.heading", "ETA & status") as string}
          </Cell>
          <EtaCard
            arrivalAt={null}
            confidence={null}
            borderBuffer={null}
            ownerCanOverride={isOwner}
          />
          <Section header={t("dispatcher:trip.stops.heading", "Stops") as string}>
            {orderedStops.map((s) => (
              <Cell
                key={s.id}
                data-testid={`trip-details-stop-${s.id}`}
                subhead={t(`dispatcher:trip.stops.role.${s.role}`, s.role) as string}
                multiline
              >
                {s.address ?? "—"}
              </Cell>
            ))}
          </Section>
          {isOwner && (
            <div style={{ padding: 8, paddingBottom: SAFE_AREA_BOTTOM }}>
              <Button
                mode="bezeled"
                size="l"
                stretched
                data-testid="trip-details-assign-carrier"
                onClick={() => navigate("/dispatcher/carriers")}
              >
                {t("dispatcher:trip.assign_carrier", "Assign external carrier") as string}
              </Button>
            </div>
          )}
        </Section>
      )}

      {tab === "route" && (
        <Section data-testid="trip-details-route">
          <Timeline active={timelineActive} data-testid="trip-details-route-timeline">
            {orderedStops.map((s, idx) => (
              <Timeline.Item
                key={s.id}
                data-testid={`trip-details-route-stop-${s.id}`}
                header={`${idx + 1}. ${t(`dispatcher:trip.stops.role.${s.role}`, s.role) as string}`}
                mode={
                  idx < timelineActive
                    ? "pre-active"
                    : idx === timelineActive
                      ? "active"
                      : undefined
                }
              >
                {s.address ?? "—"}
              </Timeline.Item>
            ))}
          </Timeline>
        </Section>
      )}

      {tab === "timeline" && (
        <Section data-testid="trip-details-timeline">
          {trip.events.length === 0 ? (
            <Cell>{t("dispatcher:trip.timeline.empty", "No events yet") as string}</Cell>
          ) : (
            trip.events.map((ev) => (
              <Cell
                key={ev.id}
                data-testid={`trip-details-event-${ev.id}`}
                subtitle={new Date(ev.occurred_at).toLocaleString()}
                multiline
              >
                {ev.event_type}
              </Cell>
            ))
          )}
        </Section>
      )}

      {tab === "documents" && (
        <Section data-testid="trip-details-documents">
          <Banner type="section" header={t("dispatcher:trip.cmr.pending", "CMR pending") as string}>
            {
              t(
                "dispatcher:trip.cmr.pending_body",
                "Driver will upload CMR on pickup. You'll be notified.",
              ) as string
            }
          </Banner>
        </Section>
      )}

      {tab === "visibility" && isOwner && (
        <Section
          data-testid="trip-details-visibility"
          footer={
            t(
              "dispatcher:trip.visibility.description",
              "Customer tracker shows the toggles enabled below.",
            ) as string
          }
        >
          {/* Locked rows — ETA + Status. Display as disabled switches. */}
          {(["eta", "status"] as const).map((field) => (
            <Cell
              key={field}
              data-testid={`visibility-row-${field}`}
              description={
                t("dispatcher:trip.visibility.locked_caption", "(locked by GDPR Art. 25)") as string
              }
              after={
                <Switch
                  checked
                  disabled
                  aria-label={field}
                  data-testid={`visibility-toggle-${field}`}
                />
              }
              multiline
            >
              {t(`dispatcher:trip.visibility.field.${field}`, field) as string}
            </Cell>
          ))}
          {TOGGLEABLE_FIELDS.map((field) => {
            const current =
              typeof visibilitySettings[field] === "boolean" ? visibilitySettings[field] : true;
            return (
              <Cell
                key={field}
                data-testid={`visibility-row-${field}`}
                after={
                  <Switch
                    checked={current}
                    aria-label={field}
                    data-testid={`visibility-toggle-${field}`}
                    onChange={(e) => {
                      const next = { ...visibilitySettings, [field]: e.target.checked };
                      setLocalVisibility(next);
                      visibilityMutation.mutate({ [field]: e.target.checked });
                    }}
                  />
                }
              >
                {t(`dispatcher:trip.visibility.field.${field}`, field) as string}
              </Cell>
            );
          })}
          {visibilityMutation.isError && (
            <Banner
              type="section"
              data-testid="visibility-error"
              header={
                t("dispatcher:trip.visibility.save_failed", "Failed to save visibility.") as string
              }
            />
          )}
        </Section>
      )}
    </List>
  );
}
